import React, { useState, useEffect } from 'react';
import Layout from '../components/common/Layout';
import DuplicateTemplateModal from '../components/common/DuplicateTemplateModal';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';
import type { Template, UserResponse } from '../types';
import { all } from 'axios';

const ClientDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [financialTemplates, setFinancialTemplates] = useState<Template[]>([]);
  const [userResponses, setUserResponses] = useState<UserResponse[]>([]);
  const [financialSession, setFinancialSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [templateToDuplicate, setTemplateToDuplicate] = useState<Template | null>(null);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<{[key: string]: boolean}>({});

  // Fetch organization templates and user responses from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        if (user?.organizationId && user?.id) {
          // Fetch templates and user responses in parallel
          let templatesPromise: Promise<Template[]> = apiService.getTemplatesViaType('management');
          
          // Fetch user responses and financial templates
          const responsesPromise = apiService.getUserResponses(user.id);
          const financialTemplatesPromise = apiService.getTemplatesViaType('financial');
          
          // Fetch active financial session
          let sessionData = null;
          try {
            const sessionResponse = await apiService.getActiveFinancialSession();
            sessionData = sessionResponse;
            console.log('Active financial session:', sessionData);
          } catch (error) {
            console.error('Failed to fetch active financial session:', error);
          }
          
          // Test API call to financial exercise sessions
          // try {
          //   const sessionData = await apiService.getFinancialExerciseSession(1);
          //   console.log('Financial exercise session data:', sessionData);
          // } catch (error) {
          //   console.error('Failed to fetch financial exercise session:', error);
          // }
          
          // Test API call to reset financial exercise session
          // try {
          //   const resetData = await apiService.resetFinancialExerciseSession(2);
          //   console.log('Financial exercise session reset:', resetData);
          // } catch (error) {
          //   console.error('Failed to reset financial exercise session:', error);
          // }
          
          const [orgTemplates, responses, finTemplates] = await Promise.all([templatesPromise, responsesPromise, financialTemplatesPromise]);
          setTemplates(orgTemplates);
          setUserResponses(responses);
          setFinancialTemplates(finTemplates);
          setFinancialSession(sessionData);
        } else {
          setTemplates([]);
          setUserResponses([]);
          setFinancialTemplates([]);
          setError('No organization or user ID associated with this account');
        }
        
        setError(null);
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('Failed to load worksheets and responses');
        setTemplates([]);
        setUserResponses([]);
        setFinancialTemplates([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleDuplicateTemplate = (template: Template) => {
    setTemplateToDuplicate(template);
    setShowDuplicateModal(true);
  };

  const handleConfirmDuplicate = async (name: string, description: string) => {
    if (!templateToDuplicate) return;
    
    try {
      setIsDuplicating(true);
      
      // Call the duplicate API with the template ID, name, and description
      await apiService.duplicateTemplate(templateToDuplicate.id, name, description);
      
      toast.success(`Successfully duplicated "${templateToDuplicate.name}"`);
      
      // Refresh the templates list to show the new duplicated template
      const allTemplates = await apiService.getAllTemplates();
      const orgTemplates = allTemplates.filter(template => template.id !== 1);
      setTemplates(orgTemplates);
      
      // Refresh user responses as well
      if (user?.id) {
        const responses = await apiService.getUserResponses(user.id);
        setUserResponses(responses);
      }
      
      // Close modal
      setShowDuplicateModal(false);
      setTemplateToDuplicate(null);
    } catch (err) {
      console.error('Failed to duplicate template:', err);
      toast.error('Failed to duplicate worksheet');
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleCloseDuplicateModal = () => {
    setShowDuplicateModal(false);
    setTemplateToDuplicate(null);
  };

  // Helper function to calculate completion status for a template
  const getTemplateCompletionStatus = (template: Template) => {
    const totalQuestions = template.questions?.length || 0;
    
    // Get unique question IDs that have at least one response
    const questionsWithResponses = new Set(
      userResponses
        .filter(response => response.template_id === template.id)
        .map(response => response.question_id)
    );
    
    const completedQuestions = questionsWithResponses.size;
    const percentage = totalQuestions > 0 ? Math.round((completedQuestions / totalQuestions) * 100) : 0;
    const isComplete = percentage >= 100;
    
    return {
      completed: completedQuestions,
      total: totalQuestions,
      percentage,
      isComplete,
      checkmark: isComplete ? ' ✓' : ''
    };
  };

  // Helper function to get the correct assignment date for a template
  const getTemplateAssignmentDate = (template: Template): string => {
    if (!user?.created_at) {
      // Fallback to template creation date if user created_at is not available
      return new Date(template.created_at).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
    
    const templateDate = new Date(template.created_at);
    const userDate = new Date(user.created_at);
    
    // Use the later date between template creation and user creation
    const assignmentDate = userDate > templateDate ? userDate : templateDate;
    
    return assignmentDate.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Helper function to get financial session button state
  const getFinancialButtonState = () => {
    const session = financialSession?.session;
    
    if (!session) {
      return {
        text: 'Start Learning',
        link: `/financial/welcome`
      };
    }
    
    if (session.isComplete) {
      return {
        text: 'Practice Again',
        link: `/financial/practice`
      };
    }
    
    return {
      text: 'Continue Learning',
      link: `/financial/practice`
    };
  };

  // Helper function to get financial progress display
  const getFinancialProgress = (template: Template) => {
    const progress = financialSession?.progress;
    const session = financialSession?.session;
    
    if (!progress) {
      // Fallback to old completion status
      const fallback = getTemplateCompletionStatus(template);
      return {
        ...fallback,
        isComplete: fallback.percentage >= 100,
        checkmark: fallback.percentage >= 100 ? ' ✓' : ''
      };
    }
    
    const percentage = progress.percentComplete || 0;
    const isComplete = percentage >= 100;
    
    return {
      completed: progress.completedScenarios || 0,
      total: progress.totalScenarios || 0,
      percentage,
      isComplete,
      checkmark: isComplete ? ' ✓' : ''
    };
  };

  // Helper function to toggle group collapse
  const toggleGroupCollapse = (groupName: string) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  // Helper function to render template card
  const renderTemplateCard = (template: Template, isFinancial = false) => {
    const borderColor = isFinancial ? 'border-l-green-500' : 'border-l-purple-500';
    const completionStatus = isFinancial ? getFinancialProgress(template) : getTemplateCompletionStatus(template);
    const buttonState = isFinancial ? getFinancialButtonState() : null;
    
    return (
      <div key={template.id} className={`bg-white border border-gray-200 rounded-lg shadow-sm border-l-4 ${borderColor} flex relative`}>
        <div className="flex-shrink-0 p-4 flex items-center justify-center bg-gray-50 w-16">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d={isFinancial 
                ? "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                : "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"} />
          </svg>
        </div>
        
        <div className="flex-1 p-4">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
            </div>
          </div>
        
          {isFinancial && <p className="text-gray-600 mb-4">{template.description}</p>}
        
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              <p>Assigned: {getTemplateAssignmentDate(template)} | {completionStatus.total || template.questions?.length || 0} {isFinancial ? 'practice scenario' : 'question'}{(completionStatus.total || template.questions?.length || 0) !== 1 ? 's' : ''}</p>
            </div>
          </div>

          {/* Completion Status Badges */}
          <div className="flex gap-2 mt-4">
            <span className={`inline-flex items-center px-4 py-2 rounded text-xs font-medium border ${
              isFinancial ? 'bg-gray-100 text-gray-800 border-gray-200' : 'bg-sky-100 text-sky-800 border-sky-200'
            }`}>
              📝 {completionStatus.completed}/{completionStatus.total} {isFinancial ? 'scenarios' : 'questions'} answered{isFinancial ? completionStatus.checkmark : ''}
            </span>
            <span className={`inline-flex items-center px-4 py-2 rounded text-xs font-medium border ${
              completionStatus.percentage >= 100 
                ? 'bg-green-100 text-green-800 border-green-200' 
                : completionStatus.percentage >= 50 
                ? 'bg-yellow-100 text-yellow-800 border-yellow-200' 
                : 'bg-red-100 text-red-800 border-red-200'
            }`}>
              {completionStatus.percentage}% complete
            </span>
          </div>

          <div className="flex gap-3 mt-4">
            <button 
              className={`inline-flex items-center px-4 py-2.5 border border-transparent text-base font-medium rounded-md text-white ${
                isFinancial 
                  ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' 
                  : 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-500'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors`}
              onClick={() => {
                if (isFinancial && buttonState) {
                  navigate(buttonState.link);
                } else if (user?.organizationId) {
                  // Check if user has viewed management training
                  const hasViewedTraining = user?.hasViewedManagementTraining;
                  if (hasViewedTraining) {
                    // Skip training page and go directly to worksheet
                    navigate(`/organization/${user.organizationId}/user/${user.id}/responses`);
                  } else {
                    // First time - show training page
                    navigate(`/organization/${user.organizationId}/user/${user.id}/welcome`);
                  }
                } else {
                  toast.error('Organization not found');
                }
              }}
            >
              {isFinancial && buttonState ? buttonState.text : (completionStatus.completed > 0 ? 'Continue Working' : 'Start Working')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Group templates by skill categories
  const skillGroups = {
    '1. Technical Skills': financialTemplates,
    '2. Communication Skills': [],
    '3. Leadership Skills': [],
    '4. Management Skills': templates,
    '5. Critical Thinking Skills': []
  };

  const renderSkillGroup = (groupName: string, groupTemplates: Template[]) => {
    const isCollapsed = collapsedGroups[groupName];
    const shouldBeCollapsible = groupTemplates.length > 2;
    const templatesToShow = (shouldBeCollapsible && isCollapsed) ? groupTemplates.slice(0, 2) : groupTemplates;
    const isFinancial = groupName === '1. Technical Skills';

    return (
      <div key={groupName} className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-gray-900">{groupName}</h2>
            {groupTemplates.length === 0 && (
              <span className="text-sm text-gray-500 italic">No templates yet</span>
            )}
          </div>
          {shouldBeCollapsible && (
            <button
              onClick={() => toggleGroupCollapse(groupName)}
              className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            >
              {isCollapsed ? (
                <>
                  <span>Show All ({groupTemplates.length})</span>
                  <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </>
              ) : (
                <>
                  <span>Show Less</span>
                  <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </>
              )}
            </button>
          )}
        </div>
        
        {groupTemplates.length > 0 && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {templatesToShow.map((template) => renderTemplateCard(template, isFinancial))}
            </div>
            
            {shouldBeCollapsible && isCollapsed && (
              <div className="mt-4 text-center">
                <button
                  onClick={() => toggleGroupCollapse(groupName)}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  + {groupTemplates.length - 2} more templates
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading worksheets...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
            <h1 className="text-3xl font-bold">
              <span style={{ color: '#F39C12' }}>QFM</span> Learning Library
            </h1>
          <p className="mt-2 text-gray-600">
            Click on the links below to select learning templates and documents
          </p>
        </div>
        
        {error && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Note</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {templates.length === 0 && financialTemplates.length === 0 && !error ? (
          <div className="text-center py-12">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Worksheets Available</h3>
            <p className="text-gray-600">There are no worksheets associated with your organization yet.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(skillGroups).map(([groupName, groupTemplates]) => 
              renderSkillGroup(groupName, groupTemplates)
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ClientDashboard;
